import { fromJS, List as ImmutableList, Map as ImmutableMap } from 'immutable';
import { DEALS_FETCH_STARTED, SET_ADDITIONAL_QUERY_PARAMS, SET_FILTER, SET_INITIAL_FILTERS } from './ActionTypes';
import { fetchDeals } from './DealsActions';
import { getAggregatedDealRollups, getAggregatedGoals, getAggregatedSubmissions, toggleAggregatedGoalsFetching, toggleAggregatedSubmissionsFetching } from './AggregatedOverviewDataActions';
import { isTimePeriodCustom } from '../utils/dateUtils';
import { isFilteredToMultiButNotAllPipelines } from '../utils/filters';
import { getFiltersFromLocalStorage, getFiltersFromQueryParams, filterStateToEventProperties, sanitizeInitialFilters } from '../utils/filters';
import { tracker } from '../utils/usageTracker';
import { MULTI_BUT_NOT_ALL_PIPELINES } from '../constants/Pipelines';
import { USE_FILTERS } from '../constants/Events';
import { REP_DETAIL, REP_OVERVIEW } from '../constants/Events';
import { CUSTOM_DATE_OPTION, FILTERS, ADDITIONAL_QUERY_PARAMS } from '../constants/FiltersConstants';
import { getSelectedDetailOwnerId } from '../selectors/detailViewSelectors';
import { getFiscalOffset } from '../selectors/portalSettings';
import { getIsUngatedToForecastingMultipleOutcomes } from '../selectors/authSelectors';
import { getForecastTypes, getPipelines } from '../selectors/forecastSelectors';
import { getDealSidebarId } from '../selectors/overviewSelectors';
import { getDealTablePagination, getDealTableSort, getIsDefaultSort } from '../selectors/dealsSelectors';
import { convertPaginationStateForApi, convertSortStateForApi } from '../utils/deals';

// Do not use this directly as it does not refetch data
export function setFilterValue(filter, value, track = false, screen = null, isPipelineFilteredToMultiPipelines = false) {
  if (track) {
    let trackValue = value;
    if (filter === FILTERS.TIME_PERIOD && isTimePeriodCustom(fromJS(value))) {
      trackValue = CUSTOM_DATE_OPTION;
    }
    if (isPipelineFilteredToMultiPipelines) {
      trackValue = MULTI_BUT_NOT_ALL_PIPELINES;
    }
    tracker.track(USE_FILTERS, Object.assign({
      screen
    }, filterStateToEventProperties(ImmutableMap({
      [filter]: trackValue
    }))));
  }
  return {
    type: SET_FILTER,
    filter,
    value
  };
}
export function setInitialFilters(intitialTeamValue) {
  return (dispatch, getState) => {
    var _getFiscalOffset, _window, _allPipelines$first;
    const state = getState();
    const allPipelines = getPipelines(state);
    const allForecastTypes = getForecastTypes(state);
    const localFilters = getFiltersFromLocalStorage();
    const fiscalOffset = (_getFiscalOffset = getFiscalOffset(state)) !== null && _getFiscalOffset !== void 0 ? _getFiscalOffset : 0;
    const isUngatedToForecastingMultipleOutcomes = getIsUngatedToForecastingMultipleOutcomes(state);
    const queryParams = new URLSearchParams((_window = window) === null || _window === void 0 || (_window = _window.location) === null || _window === void 0 ? void 0 : _window.search);
    if (queryParams.get(ADDITIONAL_QUERY_PARAMS.CONTINUE_SETUP)) {
      dispatch({
        type: SET_ADDITIONAL_QUERY_PARAMS,
        payload: {
          continueSetup: Boolean(queryParams.get(ADDITIONAL_QUERY_PARAMS.CONTINUE_SETUP))
        }
      });
    }
    const queryParamsFilters = getFiltersFromQueryParams(queryParams);
    if (queryParamsFilters.get(FILTERS.PIPELINE) || queryParamsFilters.get(FILTERS.TIME_PERIOD) || queryParamsFilters.get(FILTERS.DEAL_GROUPING_TYPE) || isUngatedToForecastingMultipleOutcomes && queryParamsFilters.get(FILTERS.FORECAST_TYPE)) {
      return dispatch({
        type: SET_INITIAL_FILTERS,
        filterState: sanitizeInitialFilters({
          filters: queryParamsFilters,
          allPipelines,
          allForecastTypes,
          fiscalOffset
        })
      });
    }
    if (localFilters) {
      return dispatch({
        type: SET_INITIAL_FILTERS,
        filterState: sanitizeInitialFilters({
          filters: localFilters,
          allPipelines,
          allForecastTypes,
          fiscalOffset
        })
      });
    }

    // set required default filter values when there is no local storage or query params
    dispatch(setFilterValue(FILTERS.PIPELINE, [(_allPipelines$first = allPipelines.first()) === null || _allPipelines$first === void 0 ? void 0 : _allPipelines$first.get('pipelineId')]));
    return dispatch(
    // ImmutableList to be removed in https://git.hubteam.com/HubSpot/forecasting-team/issues/2686
    setFilterValue(FILTERS.TEAM, ImmutableList(intitialTeamValue)));
  };
}
export function setFilter({
  filter,
  value
}) {
  return (dispatch, getState) => {
    if (value === null || value === undefined) {
      return Promise.resolve();
    }
    const state = getState();
    const sidebarId = getDealSidebarId(state);
    const pipelines = getPipelines(state);
    const isMultiButNotAllPipelines = isFilteredToMultiButNotAllPipelines(pipelines, value);
    const selectedDetailOwnerId = getSelectedDetailOwnerId(state);
    const ownerId = selectedDetailOwnerId || sidebarId;
    const isDetail = !!selectedDetailOwnerId;
    const screen = isDetail ? REP_DETAIL : REP_OVERVIEW;
    const isPipelineFilteredToMultiPipelines = filter === 'pipeline' && isMultiButNotAllPipelines;
    dispatch(setFilterValue(filter, Array.isArray(value) ? ImmutableList(value) : value, true, screen, isPipelineFilteredToMultiPipelines));
    const shouldUpdateSubmissionsAndGoals = filter === FILTERS.PIPELINE || filter === FILTERS.TIME_PERIOD || filter === FILTERS.TEAM || filter === FILTERS.FORECAST_TYPE || filter === FILTERS.DEAL_GROUPING_TYPE;
    const shouldUpdateDealData = isDetail || sidebarId;

    // dispatch loading states to prevent flashes
    if (shouldUpdateSubmissionsAndGoals) {
      dispatch(toggleAggregatedSubmissionsFetching(true));
      dispatch(toggleAggregatedGoalsFetching(true));
    }
    if (shouldUpdateDealData) {
      dispatch({
        type: DEALS_FETCH_STARTED
      });
    }
    return dispatch(getAggregatedDealRollups()).then(() => {
      if (shouldUpdateSubmissionsAndGoals) {
        Promise.all([dispatch(getAggregatedSubmissions()), dispatch(getAggregatedGoals())]).catch(() => {
          // should be caught in dispatched calls
        });
      }
      if (shouldUpdateDealData) {
        const id = ownerId || sidebarId;
        if (id) {
          Promise.all([dispatch(fetchDeals(String(id)))]).catch(() => {
            // should be caught in fetchDeals
          });
        }
      }
    });
  };
}
export function setDealTableFilter(filter, value, ownerId, isDetailPage) {
  return (dispatch, getState) => {
    if (ownerId === null) {
      return Promise.resolve();
    }
    const state = getState();
    const pagination = getDealTablePagination(state);
    const sort = getDealTableSort(state);
    const isDefaultSort = getIsDefaultSort(state);
    dispatch(setFilterValue(filter, value, true, isDetailPage ? REP_DETAIL : REP_OVERVIEW));
    return dispatch(fetchDeals(ownerId, isDetailPage ? Object.assign({}, convertPaginationStateForApi(pagination), {
      sorts: convertSortStateForApi(sort, isDefaultSort)
    }) : undefined));
  };
}